import React from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { createLocale } from 'locales';

import Typography from 'components-new/typography/typography';

import useGetFreelancerDetails from 'state/remote/remote-organization/use-get-freelancer-details';

import Button from 'components-new/button/button';

import useConfirmPeriodJob from 'state/remote/remote-organization/use-confirm-period-job';

import Modal from 'components-new/modal/modal';

import EmptyState from 'components-new/empty-state/empty-state';

import useGetPeriodJobDetails from 'state/remote/remote-organization/use-get-period-job-details';

import LoadingPage from 'pages/LoadingPage';

import RatingStars from './RatingStars';
import PeriodJobDetails from './PeriodJobDetails';
import FreelancerAvailability from './FreelancerAvailability';

const localize = createLocale('organisationPeriodJobsLocales');

const ConfirmationSection: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    periodJob,
    error: periodJobError,
    isLoading: periodJobLoading,
  } = useGetPeriodJobDetails(parseInt(searchParams.get('job_id') || '0', 10));

  const {
    data: freelancerDetails,
    error: freelancerError,
    isLoading: freelancerLoading,
  } = useGetFreelancerDetails(
    searchParams.get('freelancer_id') || '0',
    periodJob.id?.toString() || '',
    'period_job',
  );

  const navigate = useNavigate();

  const { confirmPeriodJob, isLoading } = useConfirmPeriodJob(() =>
    navigate('/organisation/period_jobs'),
  );

  if (periodJobLoading || freelancerLoading) return <LoadingPage />;

  if (periodJobError)
    return (
      <EmptyState errorText={periodJobError} title="Period job not found" />
    );

  return (
    <>
      <div className="w-full my-5 mx-6 md:mx-auto pb-32">
        <div className="flex flex-col sm:flex-row  items-start justify-start sm:justify-between">
          {!freelancerLoading && freelancerError && (
            <div className="w-full flex items-center justify-center py-5 mx-5">
              <EmptyState errorText="Freelancers details not found" />
            </div>
          )}
          {!!freelancerDetails &&
            !freelancerError &&
            !freelancerLoading &&
            !!periodJob && (
              <>
                <div className="border border-solid  border-gray-100 shadow-6 overflow-y-scroll py-6 pt-3 px-6 rounded-md flex flex-col items-center justify-start h-[400px] w-full sm:w-1/2 lg:w-2/3 xl:w-3/4">
                  <div className="flex items-start justify-start w-full">
                    <img
                      src={freelancerDetails.picture}
                      alt={freelancerDetails.name}
                      className="h-[82px] w-[82px] object-cover border-solid border-2 mr-3 rounded-full bg-contain text-caption flex items-center justify-center text-center"
                    />
                    <div className="flex flex-col items-start justify-start">
                      <Typography as="h4" variant="body-large-bold">
                        {freelancerDetails.name}
                      </Typography>
                      <RatingStars
                        maxRating={5}
                        rating={parseFloat(
                          freelancerDetails?.average_rating || '0',
                        )}
                      />
                      <Typography
                        as="h4"
                        variant="body-medium"
                        className="text-gray-600"
                      >
                        {freelancerDetails.city}
                      </Typography>
                    </div>
                  </div>
                  <Typography as="p" variant="body-large" className="mt-4 mb-6">
                    {freelancerDetails.about}
                  </Typography>
                  <div className="flex flex-col lg:flex-row items-start justify-start lg:justify-between w-full">
                    <div className="flex flex-col w-full lg:w-1/3  items-start justify-start">
                      <Typography as="h3" variant="body-large-bold">
                        {localize('competences')}
                      </Typography>
                      <ul className="mt-2 list-disc">
                        {!!freelancerDetails.competences &&
                          freelancerDetails.competences.map((comp) => (
                            <li key={comp} className="ml-4">
                              <Typography
                                as="h4"
                                variant="body-large"
                                className="text-gray-700"
                              >
                                {comp}
                              </Typography>
                            </li>
                          ))}
                      </ul>
                    </div>
                    <div className="flex flex-col w-full lg:w-1/3 lg:mx-4 items-start justify-start mt-4 lg:mt-0">
                      <Typography as="h3" variant="body-large-bold">
                        {localize('certificates')}
                      </Typography>
                      <ul className="mt-2 list-disc">
                        {!!freelancerDetails.certificates &&
                          freelancerDetails.certificates.map((cert) => (
                            <li key={cert} className="ml-4">
                              <Typography
                                as="h4"
                                variant="body-large"
                                className="text-gray-700"
                              >
                                {cert}
                              </Typography>
                            </li>
                          ))}
                      </ul>
                    </div>
                    <div className="flex flex-col w-full lg:w-1/3 items-start justify-start mt-4 lg:mt-0">
                      <Typography as="h3" variant="body-large-bold">
                        {localize('documents')}
                      </Typography>
                      <ul className="mt-2 list-none">
                        {!!freelancerDetails.documents &&
                          freelancerDetails.documents.map((doc, index) => (
                            <li
                              key={`${doc.name} - ${index.toString()}`}
                              className="ml-1 flex items-center justify-between"
                            >
                              <Typography
                                as="h4"
                                variant="body-large"
                                className="text-gray-700"
                              >
                                {doc.name}
                              </Typography>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <FreelancerAvailability
                  periodJob={periodJob}
                  freelancerId={
                    freelancerDetails.freelancer_id?.toString() || ''
                  }
                />
              </>
            )}
        </div>
        <Typography as="h3" variant="heading-2" className="mt-8 mb-3">
          {localize('assignmentInfo')}
        </Typography>
        {!!periodJob && !periodJobLoading && (
          <PeriodJobDetails periodJob={periodJob} className="mt-3" />
        )}
      </div>
      <div className="fixed z-20 h-16 flex px-4  bg-white py-3 bottom-0 right-0 left-0 w-full justify-between items-center border-t">
        <Button
          label={localize('back')}
          variant="secondary"
          onClick={() =>
            setSearchParams({ job_id: periodJob?.id?.toString() || '' })
          }
        />
        <Button
          label={localize('confirmFreelancer')}
          variant="primary"
          onClick={() => {
            searchParams.set('confirm_invitation', 'true');
            setSearchParams(searchParams);
          }}
          disabled={isLoading}
        />
      </div>
      <Modal
        variant="primary"
        isOpen={!!(searchParams.get('confirm_invitation') === 'true')}
        onClose={() => {
          searchParams.delete('confirm_invitation');
          setSearchParams(searchParams);
        }}
        title={localize('confirmAction')}
        footer={{
          helpText: localize('clickToProceed', { action: localize('confirm') }),
          primaryButton: (
            <Button
              label={localize('confirm')}
              variant="primary"
              onClick={() => {
                searchParams.delete('confirm_invitation');
                setSearchParams(searchParams);
                confirmPeriodJob({
                  freelancerId:
                    freelancerDetails?.freelancer_id?.toString() || '',
                  jobId: periodJob.id?.toString() || '',
                });
              }}
            />
          ),
          secondaryButton: (
            <Button
              label={localize('back')}
              variant="secondary"
              onClick={() => {
                searchParams.delete('confirm_invitation');
                setSearchParams(searchParams);
              }}
            />
          ),
        }}
      >
        <Typography as="p" variant="body-large-bold" className="text-gray-700">
          {localize('confirmFreelancerMessage')}
        </Typography>
      </Modal>
    </>
  );
};

export default ConfirmationSection;
